import { type ApolloCache, type DocumentNode, gql, useQuery } from '@apollo/client';

import { useTeam } from '../../context/TeamProvider';
import type {
  NexoyaContentLabelCategory,
  NexoyaContentLabelCategoryDeleteInput,
  NexoyaContentLabelCategoryValueDeletePreviewInput,
  NexoyaPortfolioBudgetConstraintDeleteImpact,
  NexoyaPortfolioV2,
  NexoyaQueryDeleteContentLabelCategoryPreviewArgs,
} from '../../types';

/**
 * What deleting a label category or label does to budget constraints. The splits
 * ride a second document so a failure there never hides the main preview; neither
 * selects `referenceDailyBudget`, which is costly and not needed here.
 */
export const CONTENT_LABEL_CATEGORY_DELETE_PREVIEW_QUERY = gql`
  query ContentLabelCategoryDeletePreview($teamId: Int!, $contentLabelCategoryId: String!) {
    deleteContentLabelCategoryPreview(teamId: $teamId, delete: { contentLabelCategoryId: $contentLabelCategoryId }) {
      budgetConstraintImpactedPortfolios {
        budgetConstraintDeletionCount
        portfolio {
          portfolioId
          title
        }
      }
    }
  }
`;

export const CONTENT_LABEL_CATEGORY_DELETE_PREVIEW_SPLITS_QUERY = gql`
  query ContentLabelCategoryDeletePreviewSplits($teamId: Int!, $contentLabelCategoryId: String!) {
    deleteContentLabelCategoryPreview(teamId: $teamId, delete: { contentLabelCategoryId: $contentLabelCategoryId }) {
      budgetConstraintImpactedPortfolios {
        portfolio {
          portfolioId
          budgetConstraints {
            contentLabelCategories {
              contentLabelCategoryId
              name
            }
          }
        }
      }
    }
  }
`;

export const CONTENT_LABEL_VALUE_DELETE_PREVIEW_QUERY = gql`
  query ContentLabelValueDeletePreview($teamId: Int!, $contentLabelCategoryValueId: String!) {
    deleteContentLabelCategoryValuePreview(
      teamId: $teamId
      delete: { contentLabelCategoryValueId: $contentLabelCategoryValueId }
    ) {
      budgetConstraintImpactedPortfolios {
        budgetConstraintDeletionCount
        portfolio {
          portfolioId
          title
        }
      }
    }
  }
`;

export const CONTENT_LABEL_VALUE_DELETE_PREVIEW_SPLITS_QUERY = gql`
  query ContentLabelValueDeletePreviewSplits($teamId: Int!, $contentLabelCategoryValueId: String!) {
    deleteContentLabelCategoryValuePreview(
      teamId: $teamId
      delete: { contentLabelCategoryValueId: $contentLabelCategoryValueId }
    ) {
      budgetConstraintImpactedPortfolios {
        portfolio {
          portfolioId
          budgetConstraints {
            contentLabelCategories {
              contentLabelCategoryId
              name
            }
          }
        }
      }
    }
  }
`;

export type DeletePreviewRow = Pick<NexoyaPortfolioBudgetConstraintDeleteImpact, 'budgetConstraintDeletionCount'> & {
  portfolio: Pick<NexoyaPortfolioV2, 'portfolioId' | 'title'>;
};

export type DeletePreviewSplitRow = {
  portfolio: Pick<NexoyaPortfolioV2, 'portfolioId'> & {
    budgetConstraints: {
      contentLabelCategories: Array<Pick<NexoyaContentLabelCategory, 'contentLabelCategoryId' | 'name'>>;
    };
  };
};

type PreviewField = 'deleteContentLabelCategoryPreview' | 'deleteContentLabelCategoryValuePreview';
// The preview field itself can come back null, so every read past it is optional.
type PreviewResponse<TField extends PreviewField, TRow> = {
  [K in TField]: { budgetConstraintImpactedPortfolios: TRow[] } | null;
};
type TeamVariable = Pick<NexoyaQueryDeleteContentLabelCategoryPreviewArgs, 'teamId'>;
type CategoryPreviewVariables = TeamVariable & Pick<NexoyaContentLabelCategoryDeleteInput, 'contentLabelCategoryId'>;
type ValuePreviewVariables = TeamVariable &
  Pick<NexoyaContentLabelCategoryValueDeletePreviewInput, 'contentLabelCategoryValueId'>;

export type DeletePreviewResult = {
  rows: DeletePreviewRow[] | undefined;
  splits: DeletePreviewSplitRow[] | undefined;
  loading: boolean;
  error?: Error;
  splitsError?: Error;
  refetch: () => void; // asks the main preview again, loading meanwhile
};

const CONTEXT = { skipGlobalErrorToast: true } as const;
// network-only for a delete's confirm, which must not trust an earlier count; cache-first for the cards' counts.
type MainFetchPolicy = 'network-only' | 'cache-first';
// Same root field and arguments as the main preview with another selection: sharing the cache entry
// would make each document overwrite the other's fields and loop the refetch.
const SPLITS_OPTIONS = { fetchPolicy: 'no-cache', context: CONTEXT } as const;

function useDeletePreview<TField extends PreviewField, TVariables extends TeamVariable>({
  documents,
  field,
  variables,
  skip,
  withSplits,
  fetchPolicy = 'network-only',
}: {
  documents: { main: DocumentNode; splits: DocumentNode };
  field: TField;
  variables: TVariables;
  skip: boolean;
  withSplits: boolean;
  fetchPolicy?: MainFetchPolicy;
}): DeletePreviewResult {
  const main = useQuery<PreviewResponse<TField, DeletePreviewRow>, TVariables>(documents.main, {
    fetchPolicy,
    context: CONTEXT,
    variables,
    skip,
    notifyOnNetworkStatusChange: true,
  });
  const splits = useQuery<PreviewResponse<TField, DeletePreviewSplitRow>, TVariables>(documents.splits, {
    ...SPLITS_OPTIONS,
    variables,
    skip: skip || !withSplits,
  });

  return {
    rows: main.data?.[field]?.budgetConstraintImpactedPortfolios,
    // A refetch (a re-open) still returns the last answer's names: report none until this read answers.
    splits: splits.loading ? undefined : splits.data?.[field]?.budgetConstraintImpactedPortfolios,
    loading: main.loading,
    error: main.error,
    splitsError: splits.error,
    // A failed refetch lands in `error` again; the rejection itself is no news.
    refetch: () => void main.refetch().catch(() => undefined),
  };
}

const CATEGORY_DOCUMENTS = {
  main: CONTENT_LABEL_CATEGORY_DELETE_PREVIEW_QUERY,
  splits: CONTENT_LABEL_CATEGORY_DELETE_PREVIEW_SPLITS_QUERY,
};
const VALUE_DOCUMENTS = {
  main: CONTENT_LABEL_VALUE_DELETE_PREVIEW_QUERY,
  splits: CONTENT_LABEL_VALUE_DELETE_PREVIEW_SPLITS_QUERY,
};

export function useCategoryDeletePreviewQuery({
  contentLabelCategoryId,
  withSplits,
  fetchPolicy,
}: {
  contentLabelCategoryId: string | null;
  withSplits: boolean;
  fetchPolicy?: MainFetchPolicy;
}): DeletePreviewResult {
  const { teamId } = useTeam();
  return useDeletePreview<'deleteContentLabelCategoryPreview', CategoryPreviewVariables>({
    documents: CATEGORY_DOCUMENTS,
    field: 'deleteContentLabelCategoryPreview',
    variables: { teamId, contentLabelCategoryId },
    skip: !contentLabelCategoryId,
    withSplits,
    fetchPolicy,
  });
}

// A budget constraint save can change what a preview counts, so the next read of one asks the server again.
export function evictDeletePreviews(cache: ApolloCache<unknown>): void {
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'deleteContentLabelCategoryPreview' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'deleteContentLabelCategoryValuePreview' });
}

export function useValueDeletePreviewQuery({
  contentLabelCategoryValueId,
  withSplits,
}: {
  contentLabelCategoryValueId: string | null;
  withSplits: boolean;
}): DeletePreviewResult {
  const { teamId } = useTeam();
  return useDeletePreview<'deleteContentLabelCategoryValuePreview', ValuePreviewVariables>({
    documents: VALUE_DOCUMENTS,
    field: 'deleteContentLabelCategoryValuePreview',
    variables: { teamId, contentLabelCategoryValueId },
    skip: !contentLabelCategoryValueId,
    withSplits,
  });
}
